/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { type ReactElement, useState } from 'react';
import { Tabs, Form, Select, Button } from 'antd';
import { type Ssp } from '../../hooks/useSsps';

type UpsertSspFormUserSyncProps = {
  currentSspId: number;
  sspId: number;
  ssps?: Ssp[];
  onFinish?: (values: unknown) => void;
};

export function UpsertSspFormUserSync({
  currentSspId,
  sspId,
  ssps = [],
  onFinish,
}: UpsertSspFormUserSyncProps): ReactElement {
  const [form] = Form.useForm();
  const [selectedSspId, setSelectedSspId] = useState<number>(sspId);

  const iframeCodeJustBidIt2xyz = `<iframe src='https://sync.justbidit2.xyz/user-sync?s=${selectedSspId}&r=SSP_REDIR_URL' style ='display:none' width='0' height='0' frameborder='0'></iframe>`;
  const iframeCodeCreativedot2net = `<iframe src='https://sync.creativedot2.net/user-sync?s=${selectedSspId}&r=SSP_REDIR_URL' style ='display:none' width='0' height='0' frameborder='0'></iframe>`;

  const preStyle = {
    whiteSpace: 'pre-wrap',
    border: '1px solid #ccc',
    padding: '10px',
    borderRadius: '4px',
    backgroundColor: '#f9f9f9',
    overflow: 'auto',
  };

  const handleFormFinish = (values: any) => {
    setSelectedSspId(values.userSyncSspId || null);
    if (onFinish) {
      if (!values.userSyncSspId) {
        values.userSyncSspId = currentSspId;
      }

      onFinish(values);
    }
  };

  return (
    <>
      <Form form={form} layout="vertical" onFinish={handleFormFinish} initialValues={{ userSyncSspId: selectedSspId }}>
        <Form.Item
          label="Select SSP for User Sync"
          name="userSyncSspId"
          extra="The SSP that will be used for user synchronization. If not selected, the current SSP will be used. Click 'Apply' and then copy the code below to provide it to the partner. This is needed for cases when partner asks for one user sync URL for multiple SSPs."
        >
          <Select
            placeholder="Select an SSP"
            style={{ width: '100%' }}
            options={ssps.map((x) => ({ label: `[${x.id}] ${x.name}`, value: x.id }))}
            showSearch
            filterOption={(input, option) =>
              ((option && option.label) || '').toLowerCase().includes(input.toLowerCase())
            }
            allowClear
          />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Apply
          </Button>
        </Form.Item>
      </Form>

      <Tabs
        defaultActiveKey="iframe"
        items={[
          {
            key: 'iframe',
            label: 'Iframe',
            children: (
              <>
                <pre style={preStyle}>{iframeCodeJustBidIt2xyz}</pre>
                <span>or</span>
                <pre style={preStyle}>{iframeCodeCreativedot2net}</pre>
              </>
            ),
          },
          {
            key: 'image',
            label: 'Image',
            children: (
              <p>
                User synchronization via image is not supported because exchange platform requires running a JavaScript
                script to sync with multiple DSPs. Using an image does not allow for the execution of JavaScript, which
                makes synchronization with multiple DSPs impossible.
              </p>
            ),
          },
        ]}
      />

      <div>
        <p>Where:</p>
        <p>
          <code>SSP_REDIR_URL</code> - URL-encoded redirect URL to SSP that shall include macro <code>{'{UID}'}</code>{' '}
          which will be replaced by actual userId.
        </p>
        <p>URL can also contain:</p>
        <p>
          <code>gdpr</code> - specifies if GDPR applies for current user (0 or 1)
        </p>
        <p>
          <code>gdpr_consent</code> - URL-encoded Base64-encoded consent string as per IAB specification
        </p>
        <p>
          <code>us_privacy</code> - URL-encoded U.S. Privacy string
        </p>
      </div>
    </>
  );
}
